'use client'

import React, { useTransition } from 'react'
import Link from 'next/link'
import { Plus } from 'lucide-react'
import { Banknote2 } from 'solar-icons'
import { getCart } from '@/lib/actions'
import { useCart } from '@/lib/hooks'

import {
  Button,
  Indicator,
  LoadingDots,
  ScrollArea,
  Separator,
} from '@/components/ui'
import { KotCardLineItem } from '@/components/molecules'

interface KotCardProps extends React.ComponentPropsWithoutRef<'div'> {
  order?: any
}

export const KotCard = ({ order, ...props }: KotCardProps) => {
  const [isFetchingOrder, startFetchingOrderData] = useTransition()
  const { setCart, cart } = useCart()

  const getOrderData = async () => {
    startFetchingOrderData(async () => {
      const res = await getCart(order?.cart?.id)
      setCart(res)
    })
  }

  React.useEffect(() => {
    getOrderData()
  }, [])

  return (
    <div
      className="group flex h-full w-full flex-col overflow-hidden rounded-lg border border-gray-100 bg-white shadow-card transition-colors"
      {...props}
    >
      {isFetchingOrder && (
        <div className="flex h-full w-full items-center justify-center">
          <LoadingDots />
        </div>
      )}

      {!isFetchingOrder && (
        <>
          <div className="flex items-center gap-3 bg-gray-50/30 px-4 py-4 md:px-8">
            <div className="flex flex-col">
              <div className="flex w-full flex-col gap-2 lg:flex-row lg:items-center">
                <h1 className="text-xl font-medium">
                  Order ID #{cart?.cart?.id}
                </h1>
                <Indicator variant="neutral" className="hidden lg:flex" />
                <h1 className="text-gray-950">{cart?.cart?.created_at}</h1>
              </div>
              <div className="flex shrink-0 text-base">
                {cart?.cart?.reference}
              </div>
            </div>
          </div>
          <Separator />
          <ScrollArea className="h-full w-full pb-10">
            <div className="flex h-full w-full flex-grow-0 flex-col">
              {cart?.cart_items?.map((item: any, index: number) => (
                <KotCardLineItem
                  item={item}
                  key={index}
                  cartId={cart?.cart?.id}
                />
              ))}
            </div>
          </ScrollArea>
          <Separator />
          <div className="flex w-full flex-col gap-3 bg-gray-50 px-3 py-3 md:flex-row">
            <Link href={`/sale?cartId=${cart?.cart?.id}`} className="w-full">
              <Button
                variant="outline"
                className="w-full shadow-card"
                size="lg"
              >
                Restore Cart
                <Plus className="size-5" strokeWidth={1.5} />
              </Button>
            </Link>

            <Link
              href={`/sale?cartId=${cart?.cart?.id}&isCheckout=true`}
              className="w-full"
            >
              <Button
                variant="default"
                size="lg"
                className="w-full shadow-card"
              >
                <Banknote2 strokeWidth={1.2} className="size-5" />
                <span>Collect Payment</span>
              </Button>
            </Link>
          </div>
        </>
      )}
    </div>
  )
}
